import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {Plus, Minus} from 'lucide-react';

export function FAQ() {
  const faqs = [
    {
      question: "How to find sun moon and rising sign?",
      answer: "To find your Sun, Moon, and Rising sign (your 'Big 3'), you need your full birth date, exact time of birth, and city of birth. Enter this information into our calculator above. The time is crucial because the Rising sign changes every 2 hours."
    },
    {
      question: "What is my rising sign?",
      answer: "Your Rising sign (or Ascendant) is the zodiac sign that was ascending on the eastern horizon at the precise moment of your birth. It represents your physical appearance, first impressions, and the way you encounter the world."
    },
    {
      question: "Can I calculate my rising sign without a birth time?",
      answer: "Technically, no. The Rising sign is time-sensitive. Without a time, you can know your Sun and likely your Moon (if it didn't change signs that day), but the Rising sign cannot be accurately determined. Some astrologers use a 'Sunrise Chart' as an alternative, but it is not your true Rising sign."
    },
    {
      question: "Why is my rising sign different from my horoscope?",
      answer: "Your 'horoscope' usually refers to your Sun sign. However, most professional horoscopes are actually written based on the Rising sign house system. Reading the horoscope for your Rising sign often provides a more accurate forecast for your life events."
    },
    {
      question: "What does 'Rising Sign Astro' mean?",
      answer: "Rising Sign Astro refers to the branch of interpretation focused on the Ascendant as the primary filter for the chart. It emphasizes that the Rising sign is the 'soul's purpose' or the vehicle the soul uses to navigate this incarnation."
    }
  ];

  return (
    <div className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-serif font-bold text-white mb-4">Frequently Asked Questions</h1>
        <p className="text-gray-400">
          Common questions about finding your signs and understanding your chart.
        </p>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <FAQItem key={index} question={faq.question} answer={faq.answer} />
        ))}
      </div>

      {/* Schema.org structured data for FAQPage */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": f.answer
            }
          }))
        })}
      </script>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string, answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-white/5 rounded-lg bg-[#131722] overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <span className="text-lg font-medium text-gray-200">{question}</span>
        {isOpen ? <Minus className="w-5 h-5 text-[#D4AF37]" /> : <Plus className="w-5 h-5 text-[#D4AF37]" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-6 pb-6 text-gray-400 leading-relaxed border-t border-white/5 pt-4">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
